import { defineStore } from "pinia";
import * as planService from "@/services/planService";

export const usePlanStore = defineStore("plan", {
  state: () => ({
    plans: [],
    selectedPlanIds: [],
    idPlan: null,
    error: null,
    totalCount: 0,
    totalPages: 1,
    currentPage: 1,
    pageSize: 10,
    searchTerm: "",
    shouldForcePageOne: false,
    selectedPlan: null,
  }),

  getters: {
    isAllOnPageSelected: (state) => (currentPageItems) => {
      if (!currentPageItems || currentPageItems.length === 0) return false;
      return currentPageItems.every((item) =>
        state.selectedPlanIds.includes(item.planId),
      );
    },
  },

  actions: {
    setIdPlan(id) {
      this.idPlan = id;
    },

    toggleSelection(id) {
      const index = this.selectedPlanIds.indexOf(id);
      if (index > -1) {
        this.selectedPlanIds.splice(index, 1);
      } else {
        this.selectedPlanIds.push(id);
      }
    },

    selectAllOnPage(ids) {
      const uniqueIds = new Set([...this.selectedPlanIds, ...ids]);
      this.selectedPlanIds = Array.from(uniqueIds);
    },

    deselectAllOnPage(idsToRemove) {
      const idsToRemoveSet = new Set(idsToRemove);
      this.selectedPlanIds = this.selectedPlanIds.filter(
        (id) => !idsToRemoveSet.has(id),
      );
    },

    clearSelection() {
      this.selectedPlanIds = [];
    },

    async fetchPlansPaged(pageNumber, pageSize, search = null) {
      try {
        const response = await planService.fetchPlansPaged(
          pageNumber,
          pageSize,
          search,
        );
        this.plans = response.data.data;
        this.totalCount = response.data.totalCount;
        this.totalPages = response.data.totalPages;
        this.currentPage = pageNumber;
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        this.error = message;
        console.error("❌ Erro ao buscar planos paginados:", message);
        throw new Error(message);
      }
    },
    /**
     * Busca todos os planos (geralmente para admin).
     */
    async fetchPlans() {
      try {
        const response = await planService.fetchPlans();
        this.plans = response.data;
        this.error = null;
      } catch (error) {
        this.error = error.response?.data || { message: error.message };
        console.error("❌ Erro ao buscar planos:", this.error);
      }
    },

    /**
     * Busca apenas os planos ativos (geralmente para exibição pública).
     */
    async fetchActivePlans() {
      try {
        const response = await planService.fetchActivePlans();
        console.log("response.data: ", response.data);
        this.plans = response.data;
        this.error = null;
      } catch (error) {
        this.error = error.response?.data || { message: error.message };
        console.error("❌ Erro ao buscar planos ativos:", this.error);
      }
    },

    async getPlanById(id) {
      try {
        const response = await planService.getPlanById(id);
        this.selectedPlan = response.data;
        return response.data;
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao obter plano:", message);
        throw new Error(message);
      }
    },

    async createPlan(data) {
      try {
        const createdPlan = await planService.createPlan(data);
        this.shouldForcePageOne = true;
        await this.fetchPlansPaged(1, this.pageSize, this.searchTerm);
        return createdPlan;
      } catch (error) {
        console.error(
          "❌ Erro ao criar plano na store:",
          error.response?.data || error.message,
        );
        throw error;
      }
    },

    async updatePlan(data) {
      try {
        await planService.updatePlan(data.planId, data);
        await this.fetchPlansPaged(
          this.currentPage,
          this.pageSize,
          this.searchTerm,
        );
      } catch (error) {
        console.error(
          "❌ Erro ao atualizar plano na store:",
          error.response?.data || error.message,
        );
        throw error;
      }
    },

    async deletePlanById(id) {
      try {
        await planService.deletePlan(id);
        // Recarrega a página atual para refletir a exclusão.
        // Se a página ficar vazia, a lógica de ajuste de página cuidará disso.
        await this.fetchPlansPaged(
          this.currentPage,
          this.pageSize,
          this.searchTerm,
        );

        if (this.plans.length === 0 && this.currentPage > 1) {
          this.currentPage -= 1;
          await this.fetchPlansPaged(
            this.currentPage,
            this.pageSize,
            this.searchTerm,
          );
        }
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao excluir plano:", message);
        throw new Error(message);
      }
    },

    async deleteSelectedPlans() {
      if (!this.selectedPlanIds.length) return;
      try {
        await planService.deletePlansBatch(this.selectedPlanIds);
        this.clearSelection();
        await this.fetchPlansPaged(
          this.currentPage,
          this.pageSize,
          this.searchTerm,
        );

        if (this.plans.length === 0 && this.currentPage > 1) {
          this.currentPage -= 1;
          await this.fetchPlansPaged(
            this.currentPage,
            this.pageSize,
            this.searchTerm,
          );
        }
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao excluir planos em lote:", message);
        throw new Error(message);
      }
    },

    setSearchTerm(term) {
      this.searchTerm = term;
      this.currentPage = 1;
      this.shouldForcePageOne = true;
      this.fetchPlansPaged(1, this.pageSize, term);
    },

    async toggleCanExport(planId) {
      try {
        const response = await planService.toggleCanExport(planId);
        const updatedPlan = response.data;
        // Atualiza o plano na lista local para reatividade imediata
        const index = this.plans.findIndex((p) => p.planId === planId);
        if (index !== -1) {
          this.plans[index] = updatedPlan;
        }
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao alternar 'CanExport':", message);
        throw new Error(message);
      }
    },

    async toggleHasDashboard(planId) {
      try {
        const response = await planService.toggleHasDashboard(planId);
        const updatedPlan = response.data;
        // Atualiza o plano na lista local para reatividade imediata
        const index = this.plans.findIndex((p) => p.planId === planId);
        if (index !== -1) {
          this.plans[index] = updatedPlan;
        }
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao alternar 'HasDashboard':", message);
        throw new Error(message);
      }
    },
    // ✅ NOVO: alterna o status ativo
    async toggleIsActive(planId) {
      try {
        const response = await planService.toggleIsActive(planId);
        const updatedPlan = response.data;
        const index = this.plans.findIndex((p) => p.planId === planId);
        if (index !== -1) {
          this.plans[index] = updatedPlan;
        }
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao alternar 'IsActive':", message);
        throw new Error(message);
      }
    },
    // ✅ NOVO (IsPublic): alterna a visibilidade pública do plano
    async toggleIsPublic(planId) {
      try {
        const response = await planService.toggleIsPublic(planId);
        const updatedPlan = response.data;
        const index = this.plans.findIndex((p) => p.planId === planId);
        if (index !== -1) {
          this.plans[index] = updatedPlan;
        }
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao alternar 'IsPublic':", message);
        throw new Error(message);
      }
    },
  },
});
